import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnInit,
  Output,
  QueryList,
  TemplateRef,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core';
import {
  FormControl,
  FormControlStatus,
  FormGroup,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';
import { Subscription } from 'rxjs';
import { IEoFormControlConfig } from '../eo-form/eo-form.config';
import { EoFormPanelConfig } from './eo-form-panel.model';

@Component({
  selector: 'eo-form-panel',
  templateUrl: './eo-form-panel.component.html',
  styleUrl: './eo-form-panel.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EoFormPanelComponent),
      multi: true,
    },
  ],
})
export class EoFormPanelComponent implements OnInit {
  @ViewChildren('formControlComponent') formComponents!: QueryList<any>;

  @ViewChild('dynamicComponentContainer', {
    read: ViewContainerRef,
    static: true,
  })
  container!: ViewContainerRef;

  private subscriptions: Subscription[] = [];
  private previousStatus?: FormControlStatus;
  @Input() customFormControls: { [key: string]: TemplateRef<any> | null } = {};

  private _config?: EoFormPanelConfig;
  @Input() set config(val: EoFormPanelConfig) {
    this._config = new EoFormPanelConfig({
      ...val,
      config: val.config.filter((x) => this.isItemVisible(x)),
    });
  }
  get config(): EoFormPanelConfig | undefined {
    return this._config;
  }
  @Input() header?: string;
  @Input() headerIcon?: string;
  @Input() headerStyleClass?: string;
  @Input() descriptionStyleClass?: string;
  @Input() description?: string;
  @Input() styleClass?: string;
  @Input() panelContentStyleClass?: string;
  @Input() formGroup!: FormGroup;
  @Output() statusChange = new EventEmitter<FormControlStatus>();
  defaultHeaderStyleClass = 'text-lg font-semibold';

  constructor() {}

  ngOnInit(): void {
    this.subscriptions = [];
  }

  ngAfterViewInit() {
    // Access all dynamically created components here
  }

  getFormControlComponents(): any {
    return this.formComponents;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s?.unsubscribe());
  }

  onStatusChange() {
    this.formGroup.updateValueAndValidity();
  }

  protected getFormType(item: IEoFormControlConfig): string {
    switch (item.type) {
      case 'custom':
        return 'custom';
      case 'dropdown':
      case 'multiselect':
      case 'countryDropdown':
      case 'specialtyDropdown':
      case 'prefectureDropdown':
      case 'cityDropdown':
      case 'userDropdown':
      case 'checkbox':
      case 'radioButton':
      case 'datepicker':
      case 'textArea':
      case 'fileUpload':
      case 'fileUploadAdvanced':
      case 'picklist':
      case 'richtext':
      case 'autocomplete':
      case 'tableField':
      case 'treeDropdown':
        return item.type;
      default:
        return 'input';
    }
  }
  protected getFormControl(formControlName: string): FormControl<any> {
    if (!this.formGroup.get(formControlName)) return new FormControl();
    return this.formGroup.get(formControlName) as FormControl;
  }
  protected getConfig(): EoFormPanelConfig | undefined {
    return this.config;
  }
  protected getFormGroup(): FormGroup {
    return this.formGroup;
  }
  protected getHeader(): string | undefined {
    return this.header ?? this.config?.header;
  }
  protected getHeaderStyleClass(): string | undefined {
    return `${this.defaultHeaderStyleClass} ${
      this.headerStyleClass ?? this.config?.headerStyleClass
    }`;
  }
  protected getHeaderIcon(): string | undefined {
    return this.headerIcon ?? this.config?.headerIcon;
  }
  protected getStyleClass(): string | undefined {
    return this.styleClass ?? this.config?.styleClass;
  }
  protected getPanelContentStyleClass(): string | undefined {
    return this.panelContentStyleClass ?? this.config?.panelContentStyleClass;
  }
  protected getDescription(): string | undefined {
    return this.description ?? this.config?.description;
  }
  protected getDescriptionStyleClass(): string | undefined {
    return (
      this.descriptionStyleClass ?? this.config?.descriptionStyleClass ?? 'mb-3'
    );
  }

  protected onRemove(item: any) {
    if (!this._config) return;

    this._config = new EoFormPanelConfig({
      ...this._config,
      config: this._config?.config.filter((x) => x !== item),
    });
  }

  writeValue(obj: any): void {}
  registerOnChange(fn: any): void {}
  registerOnTouched(fn: any): void {}

  protected isVisible(): boolean {
    if (this.config?.visible === undefined) return false;
    if (typeof this.config.visible === 'boolean') return this.config.visible;
    return this.config.visible();
  }

  isItemVisible(item: IEoFormControlConfig): boolean {
    if (item.visible === undefined) return true;
    if (typeof item.visible === 'boolean') return item.visible;
    return item.visible?.(item);
  }
}
